import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { isObjectChanged } from '@racemap/utilities/functions/isObjectChanged';
import { isLineString, isPointOnLineString } from '@racemap/utilities/functions/validation';
import type { EditableGeoObject } from '@racemap/utilities/types/geos';
import type { DrawMode, DrawModes } from '@racemap/utilities/types/geos';
import type { Feature } from 'geojson';
import { type Immutable, castDraft, produce } from 'immer';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useControl, useMap } from 'react-map-gl/mapbox';
import type { ControlPosition, IControl } from 'react-map-gl/mapbox';
import tinycolor from 'tinycolor2';
import { DirectSelectMode, type LineStringCoordinate } from './DirectSelectMode';
import { ExpandLineStringMode } from './ExpandLineStringMode';
import { PointOnLineStringMode } from './PointOnLineStringMode';
import { SimpleSelectMode } from './SimpleSelectMode';
import drawControlStyles from './drawControlStyles';
import { getUpdatedPointsOnLineString } from './utils/getUpdatedPointsOnLineString';
export type { DrawMode, DrawModes };

export type DrawControlProps = ConstructorParameters<typeof MapboxDraw>[0] & {
  features?: Immutable<Array<EditableGeoObject>>;
  position?: ControlPosition;
  mode?: DrawMode;
  selectedFeatureIds?: Array<string>;

  onCreate: (evt: { features: Array<Feature & { id: string }> }) => void;
  onUpdate: (evt: { features: Array<Feature & { id: string }>; action: string }) => void;
  onDelete: (evt: { features: Array<Feature & { id: string }> }) => void;
  onModeChange: (evt: { mode: DrawMode }) => void;
  onSelectionChange: (evt: { featureIds: Array<string> }) => void;
  onError: (evt: { error: string }) => void;
};

export const ModifyModes: ReadonlyArray<DrawMode> = [
  'direct_select',
  'point_on_line_string',
  'draw_line_string',
  'draw_point',
  'draw_polygon',
  'expand_line_string',
] as const;

const DrawControl = memo(function DrawControl({
  features,
  mode = 'simple_select',
  selectedFeatureIds,
  onCreate,
  onDelete,
  onModeChange,
  onUpdate,
  onSelectionChange,
  onError,
  ...props
}: DrawControlProps) {
  const { current: map } = useMap();
  const isProgrammaticChangeRef = useRef(false);

  const onTrash = useCallback(() => {
    const selectedFeatures = features?.filter((f) => selectedFeatureIds?.includes(f.id)) || [];
    if (selectedFeatures.length === 0) return;

    onDelete({ features: castDraft(selectedFeatures) });
  }, [onDelete, features, selectedFeatureIds]);

  useEffect(() => {
    // set custom delete function to SimpleSelectMode
    SimpleSelectMode.onTrash = onTrash;
  }, [onTrash]);

  const handleSelectionChange = useCallback(
    ({
      features,
    }: {
      features: Array<Feature & { id: string }>;
    }) => {
      // Ignore selection events fired by our own programmatic changeMode calls
      if (isProgrammaticChangeRef.current) return;
      if (
        !isObjectChanged(
          features.map((f) => f.id),
          selectedFeatureIds,
        )
      ) {
        // skip update, because features doesnt change
        return;
      }
      onSelectionChange({ featureIds: features.map((f) => f.id) });
    },
    [onSelectionChange],
  );

  const handleUpdate = useCallback(
    ({
      features: updatedFeatures,
      action,
      coordinates,
      coordinateAction,
    }: {
      features: Array<Feature & { id: string }>;
      action: 'move' | 'change_coordinates';
      coordinates?: Array<LineStringCoordinate>;
      coordinateAction?: 'add' | 'delete' | 'move';
    }) => {
      if (features != null) {
        const modifiedLineString = new Map(
          updatedFeatures.filter(isLineString).map((f) => [f.id, f]),
        );
        if (modifiedLineString.size === 0) {
          onUpdate?.({ features: updatedFeatures, action });
          return;
        }

        const updatedFeaturesWithUpdatedPointOnLineStrings = getUpdatedPointsOnLineString({
          updatedFeatures,
          features,
          coordinates,
          action,
          coordinateAction,
          onDelete,
        });

        onUpdate?.({ features: castDraft(updatedFeaturesWithUpdatedPointOnLineStrings), action });
      }
    },
    [features, onUpdate],
  );

  const drawControl = useControl<IControl>(
    () =>
      new MapboxDraw({
        styles: drawControlStyles,
        userProperties: true,
        keybindings: true,
        clickBuffer: 3,
        modes: {
          ...MapboxDraw.modes,
          point_on_line_string: PointOnLineStringMode,
          direct_select: DirectSelectMode,
          expand_line_string: ExpandLineStringMode,
          simple_select: SimpleSelectMode,
        },
        displayControlsDefault: false,
        // enable the controls, because else the delete click dont work
        controls: {
          trash: true,
        },
        ...props,
      }) as unknown as IControl,
    ({ map }) => map,
    {
      position: props.position,
    },
  ) as unknown as MapboxDraw;

  useEffect(() => {
    if (map != null) {
      map.on('draw.update', handleUpdate);
      map.on('draw.create', onCreate);
      map.on('draw.delete', onDelete);
      map.on('draw.modechange', onModeChange);
      map.on('draw.selectionchange', handleSelectionChange);

      return () => {
        map.off('draw.update', handleUpdate);
        map.off('draw.create', onCreate);
        map.off('draw.delete', onDelete);
        map.off('draw.modechange', onModeChange);
        map.off('draw.selectionchange', handleSelectionChange);
      };
    }
  }, [handleUpdate, onCreate, onDelete, onModeChange, handleSelectionChange]);

  useEffect(() => {
    if (map != null) {
      const handleTrash = () => drawControl.trash();
      map.on('draw.trash', handleTrash);
      return () => {
        map.off('draw.trash', handleTrash);
      };
    }
  }, [map, drawControl]);

  useEffect(() => {
    if (features != null) {
      const drawFeatures = convertHexColorsToRgb(features);

      drawControl.set({
        type: 'FeatureCollection',
        features: castDraft(drawFeatures),
      });
    }
  }, [features]);

  useEffect(() => {
    const featureIds = selectedFeatureIds || [];
    isProgrammaticChangeRef.current = true;

    switch (mode) {
      case 'simple_select':
        drawControl.changeMode('simple_select', {
          featureIds,
        });
        break;
      case 'direct_select':
        if (featureIds.some((id) => !isLineString(features?.find((f) => f.id === id)))) {
          // if the feature is not a line string, we can not use the direct select mode
          drawControl.changeMode('simple_select', {
            featureIds,
          });
          break;
        }

        drawControl.changeMode('direct_select', {
          featureId: featureIds[0],
        });
        break;
      case 'point_on_line_string': {
        if (featureIds.length !== 1 && featureIds.length !== 2) {
          onError({
            error: 'Point on line string mode requires exactly one or two feature to be selected',
          });
          onModeChange({ mode: 'simple_select' });
          break;
        }
        const lineString = features?.find(
          (f) => isLineString(f) && (f.id === featureIds[0] || f.id === featureIds[1]),
        );
        const selectedPointOnLineString = features?.find(
          (f) => isPointOnLineString(f) && (f.id === featureIds[0] || f.id === featureIds[1]),
        );
        if (lineString == null) {
          onError({ error: 'No line string selected' });
          onModeChange({ mode: 'simple_select' });
          break;
        }

        const existingPoints =
          features
            ?.filter(isPointOnLineString)
            .filter((f) => f.properties.lineStringId === lineString.id) || [];
        drawControl.changeMode('point_on_line_string', {
          lineStringId: lineString.id,
          pointOnLineString: selectedPointOnLineString,
          usedCoordinateIndexes: existingPoints.map((p) => p.properties.coordinateIndex),
        });
        break;
      }
      case 'expand_line_string': {
        const lineString = features?.find((f) => isLineString(f) && f.id === featureIds[0]);
        if (lineString != null) {
          drawControl.changeMode('expand_line_string', {
            featureId: lineString.id,
            from: lineString.geometry.coordinates[lineString.geometry.coordinates.length - 1],
          });
        }
        break;
      }
      default:
        drawControl.changeMode(mode.toString());
        break;
    }

    isProgrammaticChangeRef.current = false;
  }, [selectedFeatureIds, mode]);

  return null;
});

const convertHexColorsToRgb = (
  features: Immutable<Array<Feature & { id: string }>>,
): Immutable<Array<Feature & { id: string }>> => {
  return produce(features, (draft) => {
    draft.forEach((feature) => {
      if (isLineString(feature)) {
        feature.properties.color = tinycolor(
          feature.properties.color ?? RacemapColors.PaleBlue,
        ).toRgbString();
      }
    });
  });
};

export default DrawControl;
